// ═══════════════════════════════════════════════════════════════
// INICIO · Carruseles de Widgets, Animaciones LIVE y Gaming — modelo
// (destacados-modelo.js)
//
// Qué sale en cada carrusel del Inicio lo elige el equipo en el backoffice
// (Inicio → «Carruseles del Inicio»): los elementos, su orden, la imagen de
// cada uno y el texto de debajo. Se guarda en el Worker (D1
// app_config['home_showcase']) y llega a la app dentro del feed del Inicio
// (GET /api/home/feed → showcase).
//
//   { widgets: [item], animations: [item], games: [item] }
//   item = { key, image, title: { es, en, …traducciones }, text: { es, en, …traducciones } }
//     key    clave del widget o de la animación en la galería de la app, o
//            id del juego en el catálogo de Gaming.
//     image  imagen subida desde el backoffice (16:9 en Widgets y Gaming,
//            9:16 en Animaciones); vacía = la que ya tiene en la app.
//     title  nombre que sale debajo; vacío = el suyo en la app, en el idioma
//            del usuario.
//     text   frase que aparece al pasar el ratón; vacía = sin frase (o la
//            descripción que ya tenga en la app).
//     videos (solo en Gaming) hasta tres vídeos de TikTok del juego:
//            [{ id, url, cover, title, views }]. Con alguno, pulsar su tarjeta
//            abre un modal con sus portadas y «Ir al juego» (juego.js); sin
//            ninguno, va directa a su ficha. En el backoffice se pega el
//            enlace y las visualizaciones; la portada la pone el Worker al
//            guardar (la de TikTok caduca: la copia a nuestro almacenamiento).
//
// Idiomas: el backoffice escribe a mano español e inglés (EDIT_LANGS) y, con
// su asistente de traducción (un prompt para ChatGPT, como en los banners),
// rellena los otros doce de la app (LANGS). Cada usuario ve el texto de su
// idioma; si no está traducido, el inglés (si el inglés se dejó vacío, el
// español). Las traducciones son del texto en español: sin él, no se guardan.
// Un texto vacío en todos los idiomas = el nombre que ya tiene en la app, que
// la app ya sabe decir en los catorce.
//
// Una sección sin elementos (o cuyos elementos no existen en el catálogo que
// ve el usuario) se rellena sola con los más usados.
//
// DESTACADOS DE LA GALERÍA — `gallery: [{ kind, key, image, title }]` (hasta tres).
// La Galería de overlays enseña en «Todo» una fila «Destacados» con lo que elige
// el equipo: un widget, una animación o un apartado de Metas (`kind`). Solo se ve
// su imagen y su nombre, y un clic lleva a su tarjeta. Sin elegir, la fila no sale.
//  · UNA sola copia de la imagen y del nombre de cada elemento: si un destacado
//    está también en su carrusel del Inicio, se ve con la imagen y el nombre de
//    allí (cambiarlos en un sitio los cambia en los dos: `presentation()`), y su
//    entrada de `gallery` no guarda los suyos (`settleGallery()`, al guardar).
//    Si se quita del Inicio, se los lleva consigo.
//  · `gallery` AUSENTE no es `gallery: []`: lo guardado antes de existir (o por un
//    backoffice abierto de antes) no trae la lista, y eso no borra los destacados.
//
// Fuente única: lo cargan la app (destacados.js), el editor del backoffice
// (backoffice/src/lib/homeShowcase.js lo importa tal cual) y el Worker
// (cf-worker/src/lib/homeShowcase.js, que limpia con sanitize() lo que
// guarda y lo que sirve). Sin DOM.
// Expone window.TCHomeShowcase.
// TikControl © 2024-2026
// ═══════════════════════════════════════════════════════════════

(function (root) {
  'use strict';

  var SECTIONS = ['widgets', 'animations', 'games'];
  // Los idiomas de la app (renderer/i18n/locales): los dos primeros se escriben
  // a mano en el backoffice; el resto son traducciones.
  var LANGS = ['es', 'en', 'fr', 'de', 'it', 'pt', 'ru', 'tr', 'ar', 'hi', 'id', 'ja', 'ko', 'zh'];
  var EDIT_LANGS = ['es', 'en'];
  var MAX_ITEMS = 16;                       // por carrusel
  var LIMITS = { title: 40, text: 90 };     // lo que cabe debajo de una tarjeta
  // Una traducción puede salir algo más larga que su original (el alemán, el
  // ruso): se le deja este margen en vez de cortarle una palabra a medias.
  var TRANSLATION_SLACK = 1.5;

  // Proporción de la imagen de cada carrusel y tamaño al que la deja el
  // backoffice antes de subirla (el Inicio lo abre todo el mundo: que pesen poco).
  var IMAGE = {
    widgets: { ratio: '16 / 9', width: 640, height: 360 },
    animations: { ratio: '9 / 16', width: 432, height: 768 },
    games: { ratio: '16 / 9', width: 640, height: 360 },
    // Un apartado de Metas destacado en la galería (no tiene carrusel en el Inicio).
    goals: { ratio: '16 / 9', width: 640, height: 360 }
  };

  // Destacados de la Galería de overlays: de qué pueden ser y cuántos.
  var GALLERY_KINDS = ['widgets', 'animations', 'goals'];
  var MAX_GALLERY = 3;
  // Los apartados de Metas que se pueden destacar (las subpestañas de su módulo).
  var GOAL_KEYS = ['classic', 'countdown', 'gift'];

  // Carpeta de R2 de las imágenes: bajo public/popups/, la única de public/
  // que el almacenamiento sirve sin token.
  var IMAGE_PREFIX = 'public/popups/home-showcase/';
  var IMAGE_RE = /^https:\/\/storage\.tikcontrol\.(?:live|es)\/public\/popups\/home-showcase\/[A-Za-z0-9._-]{1,120}$/;
  // Claves de la galería ("gift-battle", "level_up", "anim-x2-candy") e ids de juego.
  var KEY_RE = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,63}$/;

  // Vídeos de TikTok de un juego (Gaming). El enlace, como el de «Síguenos en
  // TikTok» (cf-worker/src/lib/homeFeed.js › parseTikTokVideoUrl: mismas
  // reglas, lo vigila el test); la portada, solo de la carpeta donde el Worker
  // copia las de TikTok.
  var MAX_VIDEOS = 3;
  var VIDEO_URL_RE = /^https:\/\/(?:www\.)?tiktok\.com\/@([A-Za-z0-9._]{2,24})\/video\/(\d{8,24})(?:[/?#].*)?$/;
  var VIDEO_COVER_RE = /^https:\/\/storage\.tikcontrol\.(?:live|es)\/public\/popups\/home-tiktok\/[A-Za-z0-9._-]{1,80}$/;

  // Cómo se presenta cada widget cuando no tiene imagen del backoffice: los
  // dos colores de su fondo (su miniatura es una captura sobre negro: se
  // funde encima y queda solo el widget) o, con `cover: true`, una imagen
  // diseñada que ocupa toda la tarjeta:
  // renderer/assets/widgets/covers/<clave-con-guiones>.webp (16:9).
  // Un par por widget del catálogo (lo vigila el test).
  var WIDGET_LOOK = {
    'gift-battle': { colors: ['#ff4d6d', '#14b8a6'] },
    'battle-pk': { colors: ['#ef4444', '#3b82f6'] },
    'mvp': { colors: ['#f59e0b', '#ef4444'] },
    'timer': { colors: ['#3b82f6', '#6366f1'] },
    'top-gift': { colors: ['#d946ef', '#7c3aed'] },
    'top-combo': { colors: ['#f97316', '#ef4444'] },
    'top-likes': { colors: ['#fb7185', '#e11d48'] },
    'top-donors': { colors: ['#fbbf24', '#f97316'] },
    'top-comments': { colors: ['#38bdf8', '#2563eb'] },
    'top-points': { colors: ['#facc15', '#f59e0b'] },
    'top-rotation': { colors: ['#a855f7', '#ec4899'] },
    'carousel-fans': { colors: ['#f472b6', '#8b5cf6'] },
    'carousel-gifts': { colors: ['#fbbf24', '#ec4899'] },
    'carousel-mix': { colors: ['#38bdf8', '#a855f7'] },
    'follower-counter': { colors: ['#fe2c55', '#25f4ee'] },
    'subtitles': { colors: ['#22d3ee', '#a78bfa'] },
    'winlife': { colors: ['#facc15', '#ef4444'] },
    'reset-save': { colors: ['#ff5f6d', '#7c3aed'] },
    'follower-welcome': { colors: ['#ff4fa3', '#3ab0ff'] },
    'roulette': { colors: ['#8b5cf6', '#06b6d4'] },
    'level_up': { colors: ['#22c55e', '#14b8a6'] },
    'ranks': { colors: ['#10b981', '#0ea5e9'] },
    'auction': { colors: ['#f59e0b', '#b45309'] },
    'gift-alert': { colors: ['#ec4899', '#8b5cf6'] },
    'gift-cannon': { colors: ['#f43f5e', '#f97316'] },
    'gift-jar': { colors: ['#22d3ee', '#6366f1'] },
    'gift-jar-premium': { colors: ['#34d399', '#0d9488'] },
    'gift-gallery': { colors: ['#c084fc', '#6366f1'] },
    'firework': { colors: ['#6366f1', '#d946ef'] },
    'firework-premium': { colors: ['#f43f5e', '#a855f7'] },
    'like-fountain': { colors: ['#fb7185', '#f472b6'] },
    'chat': { colors: ['#22d3ee', '#3b82f6'] },
    'pinned-message': { colors: ['#06b6d4', '#2563eb'] },
    'event-notification': { colors: ['#fbbf24', '#d97706'] },
    'effects': { colors: ['#8b5cf6', '#4f46e5'] },
  };
  var DEFAULT_LOOK = { colors: ['#2f7bff', '#7c3aed'] };

  function widgetLook(key) {
    return WIDGET_LOOK[key] || DEFAULT_LOOK;
  }

  function cleanText(value, max) {
    return String(value == null ? '' : value).replace(/\s+/g, ' ').trim().slice(0, max);
  }

  function cleanLangs(value, max) {
    var out = {};
    var src = value && typeof value === 'object' ? value : (typeof value === 'string' ? { es: value } : {});
    EDIT_LANGS.forEach(function (lang) { out[lang] = cleanText(src[lang], max); });
    // Las traducciones: solo las que hay (lo guardado antes de tenerlas no
    // cambia de forma) y solo con texto en español, del que salen.
    if (!out.es) return out;
    var room = Math.round(max * TRANSLATION_SLACK);
    LANGS.forEach(function (lang) {
      if (EDIT_LANGS.indexOf(lang) !== -1) return;
      var text = cleanText(src[lang], room);
      if (text) out[lang] = text;
    });
    return out;
  }

  /** Idiomas (de los que se traducen) en los que un campo aún no tiene texto. Sin español, ninguno: no hay qué traducir. */
  function missingLangs(value) {
    if (!value || typeof value !== 'object' || !cleanText(value.es, 400)) return [];
    return LANGS.filter(function (lang) { return EDIT_LANGS.indexOf(lang) === -1 && !cleanText(value[lang], 400); });
  }

  /** Enlace de un vídeo de TikTok → { id, url } con la URL limpia, o null. */
  function parseVideoUrl(raw) {
    var m = VIDEO_URL_RE.exec(String(raw == null ? '' : raw).trim());
    return m ? { id: m[2], url: 'https://www.tiktok.com/@' + m[1] + '/video/' + m[2] } : null;
  }

  /** "552.1K", "1,2 M", "798500" o 798500 → entero ≥ 0 (lo que no se entiende, 0). */
  function parseViews(raw) {
    if (typeof raw === 'number') return isFinite(raw) && raw > 0 ? Math.min(Math.floor(raw), 1e12) : 0;
    var m = /^(\d+(?:[.,]\d+)?)\s*([kmb])?$/i.exec(String(raw == null ? '' : raw).trim().replace(/\s+/g, ''));
    if (!m) return 0;
    var unit = { k: 1e3, m: 1e6, b: 1e9 }[(m[2] || '').toLowerCase()];
    // Sin unidad, puntos y comas son separadores de miles («798.500»).
    var value = unit ? Number(m[1].replace(',', '.')) * unit : Number(m[1].replace(/[.,]/g, ''));
    return isFinite(value) && value > 0 ? Math.min(Math.round(value), 1e12) : 0;
  }

  /** Los vídeos de un juego: enlaces de TikTok válidos, sin repetir y con tope. */
  function cleanVideos(raw) {
    var out = [];
    var seen = {};
    (Array.isArray(raw) ? raw : []).forEach(function (entry) {
      var parsed = parseVideoUrl(entry && entry.url);
      if (!parsed || seen[parsed.id] || out.length >= MAX_VIDEOS) return;
      seen[parsed.id] = true;
      out.push({
        id: parsed.id,
        url: parsed.url,
        cover: typeof entry.cover === 'string' && VIDEO_COVER_RE.test(entry.cover.trim()) ? entry.cover.trim() : '',
        title: cleanText(entry.title, 140),
        views: parseViews(entry.views)
      });
    });
    return out;
  }

  function cleanItem(raw, section) {
    if (!raw || typeof raw !== 'object') return null;
    var key = String(raw.key == null ? '' : raw.key).trim();
    if (!KEY_RE.test(key)) return null;
    var image = typeof raw.image === 'string' && IMAGE_RE.test(raw.image.trim()) ? raw.image.trim() : '';
    var item = { key: key, image: image, title: cleanLangs(raw.title, LIMITS.title), text: cleanLangs(raw.text, LIMITS.text) };
    // Los vídeos, solo en Gaming y solo si hay alguno (el resto de elementos no cambia de forma).
    if (section === 'games') {
      var videos = cleanVideos(raw.videos);
      if (videos.length) item.videos = videos;
    }
    return item;
  }

  /** Lo guardado (o lo que manda el backoffice) → su forma conocida. Nunca lanza. */
  function sanitize(raw) {
    var out = {};
    var src = raw && typeof raw === 'object' ? raw : {};
    SECTIONS.forEach(function (section) {
      var seen = {};
      var list = [];
      (Array.isArray(src[section]) ? src[section] : []).forEach(function (entry) {
        var item = cleanItem(entry, section);
        if (!item || seen[item.key] || list.length >= MAX_ITEMS) return;
        seen[item.key] = true;
        list.push(item);
      });
      out[section] = list;
    });
    // Los destacados de la galería, solo si vienen (ausente ≠ vacío).
    if (Array.isArray(src.gallery)) out.gallery = cleanGallery(src.gallery);
    return out;
  }

  /** Los destacados de la galería: de un tipo conocido, sin repetir y con tope. */
  function cleanGallery(raw) {
    var seen = {};
    var list = [];
    (Array.isArray(raw) ? raw : []).forEach(function (entry) {
      if (!entry || typeof entry !== 'object' || list.length >= MAX_GALLERY) return;
      var kind = String(entry.kind == null ? '' : entry.kind);
      var key = String(entry.key == null ? '' : entry.key).trim();
      if (GALLERY_KINDS.indexOf(kind) === -1 || !KEY_RE.test(key)) return;
      if (kind === 'goals' && GOAL_KEYS.indexOf(key) === -1) return;
      var id = kind + '|' + key.toLowerCase().replace(/_/g, '-');
      if (seen[id]) return;
      seen[id] = true;
      list.push({
        kind: kind,
        key: key,
        image: typeof entry.image === 'string' && IMAGE_RE.test(entry.image.trim()) ? entry.image.trim() : '',
        title: cleanLangs(entry.title, LIMITS.title)
      });
    });
    return list;
  }

  function sameKey(a, b) {
    return String(a || '').toLowerCase().replace(/_/g, '-') === String(b || '').toLowerCase().replace(/_/g, '-');
  }

  function hasText(value) {
    return !!(value && typeof value === 'object' && (cleanText(value.es, 400) || cleanText(value.en, 400)));
  }

  /** El elemento del carrusel del Inicio que es el mismo que un destacado (o null). */
  function homeTwin(config, kind, key) {
    if (kind !== 'widgets' && kind !== 'animations') return null;
    var list = (config && Array.isArray(config[kind])) ? config[kind] : [];
    for (var i = 0; i < list.length; i++) if (list[i] && sameKey(list[i].key, key)) return list[i];
    return null;
  }

  /**
   * Cómo se presenta un elemento, esté donde esté: { image, title, text }.
   * Si está en su carrusel del Inicio, lo de allí; si no, lo de su destacado.
   */
  function presentation(config, kind, key) {
    var clean = sanitize(config);
    var twin = homeTwin(clean, kind, key);
    if (twin) return { image: twin.image, title: twin.title, text: twin.text, from: 'home' };
    var list = clean.gallery || [];
    for (var i = 0; i < list.length; i++) {
      if (list[i].kind === kind && sameKey(list[i].key, key)) return { image: list[i].image, title: list[i].title, text: cleanLangs(null, LIMITS.text), from: 'gallery' };
    }
    return null;
  }

  /**
   * Deja UNA copia de la imagen y del nombre de cada destacado (al guardar).
   *  · Está en su carrusel del Inicio → la copia es la de allí: lo que el destacado
   *    traía propio pasa al del Inicio si a este le falta, y el destacado se queda sin ello.
   *  · Estaba en el Inicio en lo guardado antes (`previous`) y ya no → se lleva la
   *    imagen y el nombre que tenía allí, para no cambiar de aspecto.
   * Devuelve una configuración nueva; no toca las que recibe.
   */
  function settleGallery(next, previous) {
    var config = sanitize(next);
    if (!config.gallery) return config;
    var before = previous ? sanitize(previous) : null;
    config.gallery = config.gallery.map(function (entry) {
      var twin = homeTwin(config, entry.kind, entry.key);
      if (twin) {
        if (!twin.image && entry.image) twin.image = entry.image;
        if (!hasText(twin.title) && hasText(entry.title)) twin.title = entry.title;
        return { kind: entry.kind, key: entry.key, image: '', title: cleanLangs(null, LIMITS.title) };
      }
      var was = before ? homeTwin(before, entry.kind, entry.key) : null;
      if (!was) return entry;
      return {
        kind: entry.kind,
        key: entry.key,
        image: entry.image || was.image,
        title: hasText(entry.title) ? entry.title : was.title
      };
    });
    return config;
  }
  function isEmpty(config) {
    var clean = sanitize(config);
    return SECTIONS.every(function (section) { return !clean[section].length; }) && !(clean.gallery && clean.gallery.length);
  }

  /** Las imágenes que usa una configuración (para saber cuáles sobran al guardar). */
  function images(config) {
    var clean = sanitize(config);
    var out = [];
    SECTIONS.forEach(function (section) { clean[section].forEach(function (item) { if (item.image) out.push(item.image); }); });
    (clean.gallery || []).forEach(function (item) { if (item.image) out.push(item.image); });
    return out;
  }

  /** Clave de R2 de una imagen del carrusel ('' si la URL no es una de ellas). */
  function imageKey(url) {
    var m = /^https:\/\/storage\.tikcontrol\.(?:live|es)\/(public\/popups\/home-showcase\/[A-Za-z0-9._-]{1,120})$/.exec(String(url || ''));
    return m ? m[1] : '';
  }

  /**
   * El texto de un campo en el idioma de quien mira ('' si no hay): el de su
   * idioma; sin él, el inglés (o el español); en español, el español (o el inglés).
   */
  function localized(value, lang) {
    if (!value || typeof value !== 'object') return '';
    var code = String(lang || 'es').slice(0, 2).toLowerCase();
    var es = cleanText(value.es, 400);
    var en = cleanText(value.en, 400);
    if (code === 'es') return es || en;
    // Una traducción sin su texto en español es un resto: no se enseña.
    var own = es && LANGS.indexOf(code) !== -1 ? cleanText(value[code], 400) : '';
    return own || en || es;
  }

  var api = {
    SECTIONS: SECTIONS,
    LANGS: LANGS,
    EDIT_LANGS: EDIT_LANGS,
    TRANSLATION_SLACK: TRANSLATION_SLACK,
    cleanLangs: cleanLangs,
    missingLangs: missingLangs,
    MAX_ITEMS: MAX_ITEMS,
    LIMITS: LIMITS,
    IMAGE: IMAGE,
    IMAGE_PREFIX: IMAGE_PREFIX,
    GALLERY_KINDS: GALLERY_KINDS,
    MAX_GALLERY: MAX_GALLERY,
    GOAL_KEYS: GOAL_KEYS,
    cleanGallery: cleanGallery,
    presentation: presentation,
    settleGallery: settleGallery,
    MAX_VIDEOS: MAX_VIDEOS,
    parseVideoUrl: parseVideoUrl,
    parseViews: parseViews,
    cleanVideos: cleanVideos,
    WIDGET_LOOK: WIDGET_LOOK,
    widgetLook: widgetLook,
    sanitize: sanitize,
    isEmpty: isEmpty,
    images: images,
    imageKey: imageKey,
    localized: localized
  };

  root.TCHomeShowcase = api;
  if (typeof module === 'object' && module && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
